import { useEffect, useRef } from 'react';

const steps = [
  {
    number: '01',
    icon: 'ri-calendar-check-line',
    title: 'Request Inspection',
    description: 'Submit your details online or call us. Fast, hassle-free booking.',
  },
  {
    number: '02',
    icon: 'ri-user-search-line',
    title: 'Match with Local Inspector',
    description: 'A licensed professional is assigned based on your property and location.',
  },
  {
    number: '03',
    icon: 'ri-home-gear-line',
    title: 'On-Site Inspection',
    description: 'Thorough evaluation of all major systems and structural components.',
  },
  {
    number: '04',
    icon: 'ri-file-text-line',
    title: 'Receive Report',
    description: 'Clear findings, photos, and recommendations delivered promptly.',
  },
];

export default function Process() {
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const items = sectionRef.current?.querySelectorAll('.process-step');
    if (!items) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).style.opacity = '1';
            (entry.target as HTMLElement).style.transform = 'translateY(0)';
          }
        });
      },
      { threshold: 0.12 }
    );
    items.forEach((item, i) => {
      (item as HTMLElement).style.opacity = '0';
      (item as HTMLElement).style.transform = 'translateY(20px)';
      (item as HTMLElement).style.transition = `opacity 0.55s ease ${i * 0.13}s, transform 0.55s ease ${i * 0.13}s`;
      observer.observe(item);
    });
    return () => observer.disconnect();
  }, []);

  return (
    <section id="process" ref={sectionRef} className="py-24 md:py-32 bg-[#1A1A1A]">
      <div className="max-w-7xl mx-auto px-6 md:px-14">

        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-8 h-px bg-[#C9A961]"></div>
            <span className="text-[#C9A961] text-xs font-semibold uppercase tracking-[0.2em]">The Process</span>
            <div className="w-8 h-px bg-[#C9A961]"></div>
          </div>
          <h2
            className="text-4xl md:text-5xl font-bold text-white"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            How It Works
          </h2>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 relative">
          {/* Connector line desktop */}
          <div className="hidden lg:block absolute top-[2.4rem] left-[12.5%] right-[12.5%] h-px bg-[#C9A961]/18 z-0"></div>

          {steps.map((step, i) => (
            <div key={i} className="process-step relative z-10 flex flex-col items-center text-center px-5 mb-10 lg:mb-0">
              <div className="relative mb-7">
                <div className="w-20 h-20 flex items-center justify-center rounded-full border border-[#C9A961]/25 bg-[#C9A961]/6">
                  <div className="w-13 h-13 flex items-center justify-center rounded-full bg-[#C9A961]/12">
                    <i className={`${step.icon} text-[#C9A961] text-xl`}></i>
                  </div>
                </div>
                <div className="absolute -top-1.5 -right-1.5 w-6 h-6 flex items-center justify-center bg-[#C9A961] rounded-full">
                  <span className="text-white text-[10px] font-bold leading-none">{step.number}</span>
                </div>
              </div>
              <h3
                className="text-base font-bold text-white mb-2.5"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                {step.title}
              </h3>
              <p className="text-sm text-white/80 leading-relaxed max-w-[190px]">{step.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
